import { Clock } from 'lucide-react';

/**
 * "This month is not final yet" — shown above the Employee Score table.
 *
 * A role's month is held until every parameter its scorecard needs a figure for
 * has it: an upload that has arrived, or, for a parameter read from Sales &
 * Targets, that month's sales having been used (the API's loadAwaitedUploads).
 * While it is held, employees are told "Not scored this month", but an admin still
 * sees the figures worked out so far — and without this notice those read as a
 * final month in which everybody did badly, because the missing parameter counts
 * as zero.
 *
 * It says only what a hold does. The hold keeps the month off the phone; it does
 * not change what is stored, ranked or used for the 6-month view.
 */

export interface AwaitedUpload {
  role_id: string;
  metric_def_id: number;
  code: string;
  name: string;
  /** The uploaded inputs still missing, when the parameter is built from more than one. */
  inputs: string[];
  /**
   * What the month is waiting for on this parameter: an upload, that month's
   * Sales & Targets figures, or both. Absent on an answer from an API older than
   * the Sales & Targets source, where every wait was for an upload.
   */
  feed?: 'upload' | 'sales_targets' | 'mixed';
  /** target_metrics.id of a custom parameter; null for the other kinds. */
  target_metric_id?: number | null;
}

interface Props {
  /** `meta.awaiting_uploads` of the month on screen. */
  awaiting: AwaitedUpload[];
  /** First day of the month on screen, YYYY-MM-DD. */
  month: string;
  /** The role picked on the page, or null when every role is shown. */
  roleId: string | null;
  roleNames: Map<string, string>;
}

/** 'September 2026' from the first day of a month, YYYY-MM-DD; the text as given when it is not a date. */
export const monthName = (month: string): string => {
  const d = new Date(`${String(month).slice(0, 10)}T00:00:00Z`);
  return Number.isNaN(d.getTime())
    ? month
    : d.toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });
};

const SALES_TARGETS = 'Sales & Targets';

const feedOf = (a: AwaitedUpload): NonNullable<AwaitedUpload['feed']> => a.feed ?? 'upload';

// The bracket says what is missing. Nobody uploads a Sales & Targets figure, so
// such a parameter names its source there instead of an input to go and upload.
const parameterLabel = (a: AwaitedUpload): string => {
  const feed = feedOf(a);
  if (feed === 'sales_targets') return `${a.name} (${SALES_TARGETS})`;
  if (feed === 'mixed') return `${a.name} (${[...a.inputs, SALES_TARGETS].join(', ')})`;
  return a.inputs.length > 0 ? `${a.name} (${a.inputs.join(', ')})` : a.name;
};

export default function AwaitingUploadsNotice({ awaiting, month, roleId, roleNames }: Props) {
  const shown = roleId ? awaiting.filter((a) => a.role_id === roleId) : awaiting;
  if (shown.length === 0) return null;

  const byRole = new Map<string, AwaitedUpload[]>();
  shown.forEach((a) => byRole.set(a.role_id, [...(byRole.get(a.role_id) ?? []), a]));
  const roles = [...byRole.keys()];
  const nameOf = (id: string): string => roleNames.get(id) ?? 'a role';
  const oneRole = roles.length === 1;

  // What ends the wait. "uploaded" is only true when every parameter listed is
  // waiting for an upload; one read from Sales & Targets waits for the month's
  // sales to be used instead ("its" is the month named in the heading).
  const feeds = new Set(shown.map(feedOf));
  const until = feeds.size === 1 && feeds.has('upload')
    ? `Until ${shown.length === 1 ? 'it is' : 'they are'} uploaded`
    : feeds.size === 1 && feeds.has('sales_targets')
      ? 'Until its sales are used'
      : 'Until these arrive';

  return (
    <div className="mb-3 flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
      <Clock className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" />
      <div>
        <p className="font-medium">
          {monthName(month)} is not final for {oneRole ? nameOf(roles[0]) : `${roles.length} roles`}
        </p>
        {oneRole ? (
          <p className="mt-1 text-amber-800">
            Waiting for: {byRole.get(roles[0])!.map(parameterLabel).join(', ')}.
          </p>
        ) : (
          <ul className="mt-1 list-disc space-y-0.5 pl-5 text-amber-800">
            {roles.map((id) => (
              <li key={id}>
                {nameOf(id)}: waiting for {byRole.get(id)!.map(parameterLabel).join(', ')}
              </li>
            ))}
          </ul>
        )}
        <p className="mt-1 text-amber-800">
          {until}, employees on{' '}
          {oneRole ? 'this role' : 'these roles'} see &ldquo;Not scored this month&rdquo; in the app. The scores
          below are provisional: the missing {shown.length === 1 ? 'parameter counts' : 'parameters count'} as
          zero.
        </p>
      </div>
    </div>
  );
}
